/**
 * Main App Component
 *
 * Sets up routing and wraps app with AuthProvider.
 * Includes protected routes based on user roles.
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/features/auth/AuthContext'
import Layout from '@/components/layout/Layout'
import HomePage from '@/pages/HomePage'
import SignInPage from '@/pages/SignInPage'
import SignUpPage from '@/pages/SignUpPage'
import ProjectsPage from '@/pages/ProjectsPage'
import ProjectDetailPage from '@/pages/ProjectDetailPage'
import UnitDetailPage from '@/pages/UnitDetailPage'
import BuyerDashboardPage from '@/pages/BuyerDashboardPage'
import DeveloperDashboardPage from '@/pages/DeveloperDashboardPage'
import AdminDashboardPage from '@/pages/AdminDashboardPage'
import CreateProjectPage from '@/pages/CreateProjectPage'
import ManageProjectPage from '@/pages/ManageProjectPage'
import SettingsPage from '@/pages/SettingsPage'
import ProfilePage from '@/pages/ProfilePage'
import APIDocsPage from '@/pages/APIDocsPage'
import DeveloperLandingPage from '@/pages/DeveloperLandingPage'
import ForDevelopersPage from '@/pages/ForDevelopersPage'
import ASPReviewPage from '@/pages/ASPReviewPage'
import AdminTemplatesPage from '@/pages/AdminTemplatesPage'
import AdminDeveloperManagement from '@/features/admin/components/AdminDeveloperManagement'
import AdminCreateProjectForDeveloper from '@/features/admin/components/AdminCreateProjectForDeveloper'
import AdminDeveloperDashboardView from '@/features/admin/components/AdminDeveloperDashboardView'

// Protected route wrapper
function ProtectedRoute({ children, allowedRoles = [], requireAdmin = false }) {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/signin" replace />
  }

  if (requireAdmin && !profile?.admin_level) {
    return <Navigate to="/" replace />
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(profile?.role)) {
    return <Navigate to="/" replace />
  }

  return children
}

// App routes
function AppRoutes() {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner spinner-lg text-primary-600 mb-4"></div>
          <p className="text-secondary-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/for-developers" element={<ForDevelopersPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="/projects/:projectId/units/:unitId" element={<UnitDetailPage />} />
        <Route path="/developers/:developerId" element={<DeveloperLandingPage />} />

        {/* Protected Routes - Buyer */}
        <Route
          path="/dashboard/buyer"
          element={
            <ProtectedRoute allowedRoles={['buyer']}>
              <BuyerDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/asp/:aspId"
          element={
            <ProtectedRoute>
              <ASPReviewPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Developer */}
        <Route
          path="/dashboard/developer"
          element={
            <ProtectedRoute allowedRoles={['developer']}>
              <DeveloperDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/developer/projects/create"
          element={
            <ProtectedRoute allowedRoles={['developer']}>
              <CreateProjectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/developer/projects/:id/edit"
          element={
            <ProtectedRoute allowedRoles={['developer']}>
              <ManageProjectPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Admin */}
        <Route
          path="/dashboard/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/templates"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/developers"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDeveloperManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/developers/:developerId"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDeveloperDashboardView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/developers/:developerId/create-project"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminCreateProjectForDeveloper />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/create-project"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminCreateProjectForDeveloper />
            </ProtectedRoute>
          }
        />

        {/* Profile - All authenticated users */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Settings - All authenticated users */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* API Docs - Public */}
        <Route path="/api-docs" element={<APIDocsPage />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )
}

// Main App component
export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
